import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";

type StateTone =
  | "neutral"
  | "positive"
  | "attention"
  | "critical";

type ActionProps =
  ButtonHTMLAttributes<HTMLButtonElement> & {
    children: ReactNode;
  };

export function WorkspaceHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="foundation-workspace-header">
      <div className="foundation-heading-copy">
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>

      {actions ? (
        <div
          className="foundation-header-actions"
          aria-label="Workspace actions"
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export function PageHeader({
  title,
  description,
  meta,
  actions,
}: {
  title: string;
  description?: string;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="foundation-page-header">
      <div className="foundation-heading-copy">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
        {meta ? <div className="foundation-page-meta">{meta}</div> : null}
      </div>

      {actions ? (
        <div
          className="foundation-header-actions"
          aria-label="Page actions"
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export function PageAction({
  children,
  type = "button",
  ...props
}: ActionProps) {
  return (
    <button
      {...props}
      type={type}
      className={[
        "foundation-action",
        "foundation-page-action",
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}

export function SectionHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="foundation-section-header">
      <div>
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>

      {actions ? (
        <div className="foundation-section-actions">{actions}</div>
      ) : null}
    </div>
  );
}

export function FilterBar({
  children,
  label = "Filters",
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <div
      className="foundation-filter-bar"
      role="group"
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function SearchField({
  label = "Search",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
}) {
  return (
    <label className="foundation-search-field">
      <span className="foundation-control-label">{label}</span>
      <input {...props} type="search" />
    </label>
  );
}

export function ResourceTable({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <div
      className="foundation-resource-table"
      role="region"
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function ResourceList({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <div
      className="foundation-resource-list"
      role="region"
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function DetailPanel({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <aside
      className="foundation-detail-panel"
      aria-label={title}
    >
      <div className="foundation-detail-head">
        <h3>{title}</h3>
        {actions ? (
          <div className="foundation-detail-actions">{actions}</div>
        ) : null}
      </div>

      <div className="foundation-detail-body">{children}</div>
    </aside>
  );
}

export function StateText({
  tone = "neutral",
  children,
}: {
  tone?: StateTone;
  children: ReactNode;
}) {
  return (
    <span
      className={`foundation-state-text ${tone}`}
      data-state-tone={tone}
    >
      <span
        className="foundation-state-marker"
        aria-hidden="true"
      />
      <span>{children}</span>
    </span>
  );
}

export function ConfirmAction({
  children,
  type = "button",
  ...props
}: ActionProps) {
  return (
    <button
      {...props}
      type={type}
      className={[
        "foundation-action",
        "foundation-confirm-action",
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}

export function DangerAction({
  children,
  type = "button",
  ...props
}: ActionProps) {
  return (
    <button
      {...props}
      type={type}
      data-danger-action="true"
      className={[
        "foundation-action",
        "foundation-danger-action",
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}
